/**
 * Careline's connection to Talkie over the direct bridge (`@talkie/agentic-app-transport`): no MCP server
 * runs in the page. This file registers a handler per manifest tool (`tools.ts`) and each own-app view as
 * an exact `ui://` resource, starts the bridge, and tells app.ts how it is going.
 *
 * Host bootstrap: the host appends `#v=1&k=<key>&o=<origin>` to the iframe URL. Without it the page is an
 * ordinary standalone EHR; with it the page says hello, and reads "Dobby connected" only once the host has
 * welcomed it. Every failure leaves the manual controls working.
 */

import { AppBridge, type BridgeStatus } from "@talkie/agentic-app-transport";
import { APP } from "./app-identity";
import type { Store } from "./domain.js";
import { ehrHandlers, type ViewPort } from "./tools";
import { careLineViews } from "./views/index.js";

/** The MIME an MCP App view is served as (the host's `MCP_APP_HTML_MIME`). */
const VIEW_MIME = "text/html;profile=mcp-app";

/** Register every tool handler and every view on `bridge` (before `start`). */
export function registerCareline(bridge: AppBridge, store: Store, view: ViewPort): void {
  // A review tool answers with its view's payload only when THIS host said it renders views (FR-7).
  const rendersViews = () => bridge.host?.views === true;
  for (const [name, handler] of Object.entries(ehrHandlers(store, view, rendersViews))) bridge.registerTool(name, handler);
  // One exact resource per view, so the page's `hello` names each URI the host may read.
  for (const [uri, text] of Object.entries(careLineViews)) {
    bridge.registerResource(uri, () => ({ contents: [{ uri, mimeType: VIEW_MIME, text }] }));
  }
}

/** The line `#cl-runtime-status` shows for each bridge status. */
const STATUS_LINES: Record<BridgeStatus, string> = {
  standalone: "Standalone browser",
  connecting: "Connecting to Dobby",
  connected: "Dobby connected",
  "needs-newer-talkie": "Dobby needs a newer Talkie · manual controls available",
  error: "Dobby integration unavailable · manual controls available",
};

/** Start the bridge for this page; `publish` receives the status line on every change. */
export function attachHost(store: Store, view: ViewPort, publish: (state: string) => void): AppBridge {
  const bridge = new AppBridge({
    appVersion: APP.version,
    onStatus: (status, detail) => {
      if (detail) console.info("ehr.bridge_detail", { status, detail });
      publish(STATUS_LINES[status]);
    },
  });
  registerCareline(bridge, store, view);
  bridge.start();
  return bridge;
}
